/**
 * GA4 event tracking — one funnel, two checkout paths.
 * Full tracking plan (event list, params, GA4 admin steps): GA4_TRACKING_PLAN.md (repo root)
 * Funnel events are also mirrored to the Meta Pixel (see META_EVENTS below);
 * the pixel itself loads from layouts/platform/_meta_pixel, rendered only on
 * /try landing pages — everywhere else fbq is undefined and the mirror no-ops.
 *
 * Funnel events:
 *   select_item        discover card click
 *   view_item          event detail page load
 *   generate_lead      any lead capture; lead_type param says which
 *   add_to_cart        legacy multi-event cart
 *   begin_checkout     checkout UI entered (self-book page, proposal payment page, legacy modal)
 *   add_payment_info   payment form submitted ("Pay" clicked)
 *   purchase           booking_confirmed page / cart success page
 *   proposal_viewed    fires inline from proposals/show.html.erb (no separate "accepted"
 *                      event — going through to payment IS the accept, captured by
 *                      begin_checkout with checkout_type: advisor_proposal)
 *
 * checkout_type param on checkout/purchase events: self_checkout | advisor_proposal | cart.
 * The server-side purchase (ConversionTracker) sends the same checkout_type values.
 *
 * If events don't appear in GA4:
 * 1. Confirm GOOGLE_TAG_ID is set in Heroku (Config Vars) for the app.
 * 2. In GA4: Reports → Engagement → Events (custom events show here; they are not "Key events" until you mark them).
 * 3. To show in Key events: Admin → Data display → Events → find each event → toggle "Mark as key event".
 *
 * Google Ads conversion labels are set in Google Ads → Goals → Conversions.
 * Pass the label suffix (e.g. 'JN9jCNXT9GMQpMHH1AM') — the AW-XXXXXXX/ prefix is added automatically.
 */
function sendGAEvent(eventName, eventParams) {
  eventParams = eventParams || {};
  if (typeof gtag === 'function') {
    gtag('event', eventName, eventParams);
  } else {
    (window.dataLayer = window.dataLayer || []).push(['event', eventName, eventParams]);
  }
  sendMetaEvent(eventName, eventParams);
}

// GA funnel event → Meta Pixel standard event. Events not listed here
// (select_item, proposal_viewed, ...) are GA-only.
var META_EVENTS = {
  view_item: 'ViewContent',
  add_to_cart: 'AddToCart',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'Purchase',
  generate_lead: 'Lead'
};

// Mirrors GA funnel events to the Meta Pixel. The pixel is only rendered on
// /try landing pages (layouts/platform/_meta_pixel, META_PIXEL_ID set), so
// this no-ops everywhere else — fbq is undefined.
function sendMetaEvent(gaEventName, gaParams) {
  if (typeof fbq !== 'function') return;
  var metaEventName = META_EVENTS[gaEventName];
  if (!metaEventName) return;

  var params = {};
  if (gaParams.value != null) {
    params.value = gaParams.value;
    params.currency = gaParams.currency || 'USD';
  }
  if (gaParams.items && gaParams.items.length > 0) {
    params.content_type = 'product';
    params.content_name = gaParams.items[0].item_name;
    params.content_ids = gaParams.items.map(function (i) { return String(i.item_id); });
    params.contents = gaParams.items.map(function (i) {
      return { id: String(i.item_id), quantity: i.quantity || 1, item_price: i.price };
    });
  }
  if (gaParams.lead_type) params.content_category = gaParams.lead_type;

  // eventID lets Meta dedupe against a future server-side Conversions API
  // event for the same transaction.
  if (gaParams.transaction_id) {
    fbq('track', metaEventName, params, { eventID: String(gaParams.transaction_id) });
  } else {
    fbq('track', metaEventName, params);
  }
}

// Inline view scripts (self-book, proposals) reuse this instead of redefining it.
window.tbTrack = sendGAEvent;

// callback (optional) runs once via event_callback, or after 500ms regardless —
// whichever comes first. Use it to delay a same-tick navigation (e.g. a native
// form submit) just long enough for the beacon to actually leave the browser;
// without it, gtag's dispatch can lose the race against page unload.
function sendAdsConversion(conversionLabel, value, transactionId, callback) {
  if (typeof gtag !== 'function' || !window.pmAdsID) {
    if (callback) callback();
    return;
  }
  var params = { send_to: window.pmAdsID + '/' + conversionLabel };
  if (value != null) {
    params.value = value;
    params.currency = 'USD';
  }
  if (transactionId) {
    params.transaction_id = transactionId;
  }
  if (callback) {
    var called = false;
    var proceed = function () {
      if (called) return;
      called = true;
      callback();
    };
    params.event_callback = proceed;
    gtag('event', 'conversion', params);
    setTimeout(proceed, 500);
  } else {
    gtag('event', 'conversion', params);
  }
}

// Conversion labels from Google Ads → Goals → Conversions
var ADS_CONVERSIONS = {
  contact_submission: 'TQWnCIffl60cEKTBx9QD', // "Request quote"
  purchase: 'VkrUCICAmq0cEKTBx9QD',           // "Booking Purchase" — single booking confirmation page
  multi_purchase: 'VkrUCICAmq0cEKTBx9QD',     // "Booking Purchase" — multi-booking cart checkout
  start_booking: 'gX7rCMf5sa0cEKTBx9QD',      // "Start booking"
};

// Only run if jQuery is loaded
if (typeof $ !== 'undefined' && typeof jQuery !== 'undefined') {
  $(document).ready(() => {

  var item_data = $('[data-ga-item_name]');

  // Returns an items array if product context is available on the current page, otherwise null.
  function currentItemParams() {
    if (item_data.length === 0) return null;
    var el = item_data.first();
    return [{
      item_id: el.data('ga-item_id'),
      item_name: el.data('ga-item_name'),
      price: el.data('ga-value'),
      quantity: 1
    }];
  }

  // Sums every [data-ga-item_id] on the page into { value, items } (cart + payment pages).
  function pageItemsTotal() {
    var items = [];
    var totalValue = 0;
    $('[data-ga-item_id]').each(function () {
      var item = $(this);
      var value = parseFloat(item.data('ga-value')) || 0;
      totalValue += value;
      items.push({
        item_id: item.data('ga-item_id'),
        item_name: item.data('ga-item_name'),
        price: value,
        quantity: 1
      });
    });
    return { value: totalValue, items: items };
  }

  // ---- select_item: discover search result clicked ----
  $(document).on('click', '[data-event-card] a.ds-stretched-link', function () {
    var card = this.closest('[data-event-card]');
    if (!card) return;
    var $card = $(card);
    sendGAEvent('select_item', {
      item_list_id: window.location.pathname,
      items: [{
        item_id: String($card.data('id')),
        item_name: $(this).text().trim(),
        price: parseFloat($card.data('price')) || 0,
        index: $('[data-event-card]').index(card),
        quantity: 1
      }]
    });
  });

  // ---- view_item: event detail page ----
  const pathName = window.location.pathname;
  const isProductPage = (pathName.includes('/bookings/') || pathName.includes('/activity/') ||
    /^\/(activities|originals)\/[^/]+(\/[^/]+)?/.test(pathName)) &&
    !pathName.includes('/bookings/checkout');

  if (isProductPage && item_data.length > 0) {
    const firstItem = item_data.first();
    sendGAEvent('view_item', {
      value: firstItem.data('ga-value'),
      currency: 'USD',
      items: [{
        item_id: firstItem.data('ga-item_id'),
        item_name: firstItem.data('ga-item_name'),
        price: firstItem.data('ga-value'),
        quantity: 1
      }]
    });
  }

  // ---- add_to_cart (legacy multi-event cart) ----
  window.trackAddToCart = () => {
    if (item_data.length > 0) {
      sendGAEvent('add_to_cart', {
        value: item_data.data('ga-value'),
        currency: 'USD',
        items: [{
          item_id: item_data.data('ga-item_id'),
          item_name: item_data.data('ga-item_name'),
          price: item_data.data('ga-value'),
          quantity: 1
        }]
      });
    }
  }

  // Cart page load: attribute the just-added (or first) item as add_to_cart.
  if (window.location.pathname === '/cart') {
    const cartItems = $('[data-ga-item_id]');
    if (cartItems.length > 0) {
      const fromAdd = document.referrer.includes('/add_to_cart/');
      const item = fromAdd ? cartItems.last() : cartItems.first();
      sendGAEvent('add_to_cart', {
        value: item.data('ga-value'),
        currency: 'USD',
        items: [{
          item_id: item.data('ga-item_id'),
          item_name: item.data('ga-item_name'),
          price: item.data('ga-value'),
          quantity: 1
        }]
      });
    }
  }

  $(document).on('click', 'a[href*="/add_to_cart/"]', function () {
    const bookingSlug = $(this).attr('href').split('/add_to_cart/')[1];
    const itemElement = $(`[data-ga-item_id="${bookingSlug}"], [data-booking-id="${bookingSlug}"]`);
    if (itemElement.length > 0) {
      sendGAEvent('add_to_cart', {
        value: itemElement.data('ga-value') || itemElement.data('booking-total-price'),
        currency: 'USD',
        items: [{
          item_id: itemElement.data('ga-item_id'),
          item_name: itemElement.data('ga-item_name'),
          price: itemElement.data('ga-value') || itemElement.data('booking-total-price'),
          quantity: 1
        }]
      });
    } else {
      window.trackAddToCart();
    }
  });

  // ---- begin_checkout: entering the checkout UI ----
  // Self-book customize page (marker div rendered by self_book/show.html.erb).
  var selfBookData = document.getElementById('self-book-ga-data');
  if (selfBookData) {
    sendGAEvent('begin_checkout', {
      checkout_type: 'self_checkout',
      value: parseFloat(selfBookData.getAttribute('data-ga-value')) || 0,
      currency: 'USD',
      items: [{
        item_id: selfBookData.getAttribute('data-ga-item_id'),
        item_name: selfBookData.getAttribute('data-ga-item_name'),
        price: parseFloat(selfBookData.getAttribute('data-ga-value')) || 0,
        quantity: 1
      }]
    });
  }

  // Payment page (bookings#checkout). Proposal customers enter checkout here; the
  // self-book flow already fired begin_checkout on /book/:slug (referrer guard).
  var checkoutData = document.getElementById('checkout-ga-data');
  if (checkoutData) {
    var checkoutType = checkoutData.getAttribute('data-checkout-type') || 'self_checkout';
    var cameFromSelfBook = document.referrer.indexOf('/book/') !== -1;
    if (checkoutType === 'advisor_proposal' || !cameFromSelfBook) {
      var pageTotals = pageItemsTotal();
      sendGAEvent('begin_checkout', {
        checkout_type: checkoutType,
        value: parseFloat(checkoutData.getAttribute('data-ga-total')) || pageTotals.value,
        currency: 'USD',
        items: pageTotals.items
      });
    }
  }

  // Legacy quick-book modal on cart/event pages.
  window.trackBeginCheckout = () => {
    var totals = pageItemsTotal();
    sendGAEvent('begin_checkout', {
      checkout_type: 'cart',
      value: totals.value,
      currency: 'USD',
      items: totals.items
    });
  }
  $(document).on('click', '[data-bs-target="#completeBooking"]', function () {
    window.trackBeginCheckout();
  });

  // "Start Booking" on the event page: Google Ads conversion only — the GA4
  // begin_checkout fires when the checkout UI actually loads (above).
  $(document).on('click', '#start-booking-btn', function () {
    sendAdsConversion(ADS_CONVERSIONS.start_booking);
  });

  // ---- add_payment_info: "Pay" submitted on the payment page (card or credits) ----
  $(document).on('submit', 'form[action="/checkout"]', function () {
    var data = document.getElementById('checkout-ga-data');
    var totals = pageItemsTotal();
    sendGAEvent('add_payment_info', {
      checkout_type: (data && data.getAttribute('data-checkout-type')) || 'cart',
      value: data ? (parseFloat(data.getAttribute('data-ga-total')) || totals.value) : totals.value,
      currency: 'USD',
      items: totals.items
    });
  });

  // ---- generate_lead: "Get a Custom Quote" / "Request Pricing" form ----
  // Holds the native submit (real POST + redirect) until the Ads beacon has a
  // chance to actually leave the browser — see sendAdsConversion's callback doc.
  $(document).on('submit', 'form[action*="/quotes"]', function (e) {
    var form = this;
    var params = { lead_type: 'quote_request' };
    var items = currentItemParams();
    if (items) params.items = items;
    sendGAEvent('generate_lead', params);

    e.preventDefault();
    sendAdsConversion(ADS_CONVERSIONS.contact_submission, null, null, function () {
      form.submit();
    });
  });

  // ---- purchase ----
  // Cart checkout success page (?success_payment=true&booking_ids=...).
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('success_payment') == 'true') {
    let bookingIds = urlParams.get('booking_ids')?.split(',');
    if (bookingIds === undefined) {
      const bookingId = item_data.data('booking-id');
      if (bookingId) bookingIds = [bookingId];
    }

    if (bookingIds) {
      let items = [];
      let totalPrice = 0;
      let transactionId;
      for (const bookingId of bookingIds) {
        const booking = $(`[data-booking-id="${bookingId}"]`);
        totalPrice += parseFloat(booking.data('booking-total-price'));
        transactionId = booking.data('ga-transaction_id');
        items.push({
          item_id: booking.data('ga-item_id'),
          item_name: booking.data('ga-item_name'),
          price: booking.data('booking-total-price'),
          quantity: 1
        });
      }

      sendGAEvent('purchase', {
        value: totalPrice,
        currency: 'USD',
        transaction_id: transactionId,
        checkout_type: 'cart',
        items: items
      });
      sendAdsConversion(ADS_CONVERSIONS.multi_purchase, totalPrice, transactionId);
    }
  }

  // Single booking confirmation page (self-book + proposal both land here).
  if (window.location.pathname.includes('/booking_confirmed')) {
    var conversionData = document.getElementById('booking-conversion-data');
    if (conversionData) {
      var value = parseFloat(conversionData.getAttribute('data-value')) || 0;
      var transactionId = conversionData.getAttribute('data-transaction-id');
      var purchaseParams = {
        value: value,
        currency: 'USD',
        transaction_id: transactionId,
        checkout_type: conversionData.getAttribute('data-checkout-type') || 'self_checkout'
      };
      var itemId = conversionData.getAttribute('data-item-id');
      if (itemId) {
        purchaseParams.items = [{
          item_id: itemId,
          item_name: conversionData.getAttribute('data-item-name'),
          price: value,
          quantity: 1
        }];
      }
      sendGAEvent('purchase', purchaseParams);
      sendAdsConversion(ADS_CONVERSIONS.purchase, value, transactionId);
    }
  }

  });
} // End of jQuery check
